import { useEffect, useState } from 'react';
import {
  ActionIcon, AppShell, Avatar, Badge, Burger, Button, Group, Menu, NavLink,
  ScrollArea, Select, Stack, Text, TextInput, Tooltip, useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconActivity, IconAddressBook, IconBolt, IconChartBar, IconChevronDown,
  IconLogout, IconMail, IconMoon, IconPlus, IconRoute, IconSend, IconSettings,
  IconShieldOff, IconSun, IconTemplate, IconUsersGroup,
} from '@tabler/icons-react';
import { NavLink as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { getCurrentProject, logout, setCurrentProject } from '../api/client';
import { useCreateProject, useMe, useMyProjects } from '../api/hooks';
import { EmptyProjectState } from '../components/EmptyProjectState';
import { Logo } from '../components/Logo';

const NAV = [
  { to: '/', label: 'Overview', icon: IconChartBar, end: true },
  { to: '/compose', label: 'Send', icon: IconSend },
  { to: '/messages', label: 'Messages', icon: IconMail },
  { to: '/journeys', label: 'Journeys', icon: IconRoute },
  { to: '/segments', label: 'Segments', icon: IconUsersGroup },
  { to: '/templates', label: 'Templates', icon: IconTemplate },
  { to: '/profiles', label: 'Profiles', icon: IconAddressBook },
  { to: '/events', label: 'Events', icon: IconBolt },
  { to: '/suppressions', label: 'Suppressions', icon: IconShieldOff },
  { to: '/audit', label: 'Audit log', icon: IconActivity },
  { to: '/settings', label: 'Settings', icon: IconSettings },
];

export function AppShellLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: me } = useMe();
  const { data: projects, isLoading } = useMyProjects();
  const [projectId, setProjectId] = useState<string | null>(getCurrentProject());

  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  // Settle on a project once the list arrives: the stored id if it is still one
  // the user can reach, otherwise the first. A stored id from a project they
  // have since been removed from would make every query 404.
  useEffect(() => {
    if (!projects?.length) return;
    const stored = getCurrentProject();
    const valid = projects.find((p) => p.id === stored);
    const chosen = valid ?? projects[0];
    if (chosen && chosen.id !== projectId) {
      setCurrentProject(chosen.id);
      setProjectId(chosen.id);
    }
  }, [projects, projectId]);

  // Close the mobile nav on navigation, or it covers the page it just opened.
  useEffect(() => close(), [location.pathname, close]);

  function switchProject(id: string | null) {
    if (!id || id === projectId) return;
    setCurrentProject(id);
    setProjectId(id);
    // Every query key starts with the project id, so this drops the previous
    // tenant's data rather than showing it under the new tenant's name.
    qc.clear();
    navigate('/');
  }

  const current = projects?.find((p) => p.id === projectId);

  if (!isLoading && !projects?.length) {
    return <EmptyProjectState onCreate={() => openCreateProject(me?.orgs?.[0]?.id)} />;
  }

  function openCreateProject(orgId?: string) {
    modals.open({
      title: 'New project',
      children: <CreateProjectForm orgId={orgId} onDone={(id) => switchProject(id)} />,
    });
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 232, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Group gap={6} wrap="nowrap">
              <Logo variant="lockup" size={28} wordmarkSize="md" />
            </Group>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Select
              size="xs"
              w={220}
              value={projectId}
              onChange={switchProject}
              data={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
              allowDeselect={false}
              searchable
              comboboxProps={{ withinPortal: true }}
              leftSection={<IconChevronDown size={14} />}
              aria-label="Project"
            />
            {current?.environment === 'development' && (
              <Badge color="orange" variant="light" visibleFrom="sm">
                development
              </Badge>
            )}

            <Tooltip label={colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}>
              <ActionIcon variant="subtle" color="gray" onClick={toggleColorScheme}>
                {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>

            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <Avatar size={30} radius="xl" style={{ cursor: 'pointer' }}>
                  {(me?.name || me?.email || '?').slice(0, 1).toUpperCase()}
                </Avatar>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{me?.email}</Menu.Label>
                {current?.role && (
                  <Menu.Label>
                    <Badge size="xs" variant="light" color={current.role === 'owner' ? 'brand' : 'gray'}>
                      {current.role}
                    </Badge>
                  </Menu.Label>
                )}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconPlus size={14} />}
                  onClick={() => openCreateProject(current?.org_id ?? me?.orgs?.[0]?.id)}
                >
                  New project
                </Menu.Item>
                <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => navigate('/settings')}>
                  Settings
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={async () => {
                    await logout();
                    qc.clear();
                    navigate('/login', { replace: true });
                  }}
                >
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={2}>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                component={RouterLink}
                to={item.to}
                end={item.end}
                label={item.label}
                leftSection={<item.icon size={18} stroke={1.6} />}
                style={{ borderRadius: 'var(--mantine-radius-md)' }}
              />
            ))}
          </Stack>
        </AppShell.Section>
        <AppShell.Section>
          <Text size="xs" c="dimmed" px="xs" py={4}>
            {current?.timezone ?? 'UTC'}
          </Text>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

function CreateProjectForm({ orgId, onDone }: { orgId?: string; onDone: (id: string) => void }) {
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState('production');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  const create = useCreateProject();

  return (
    <Stack>
      <TextInput
        label="Name"
        placeholder="Acme Rewards"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        data-autofocus
      />
      <Select
        label="Environment"
        data={['production', 'development']}
        value={environment}
        onChange={(v) => setEnvironment(v ?? 'production')}
      />
      <TextInput
        label="Timezone"
        description="Used for quiet hours and daily rollups"
        value={timezone}
        onChange={(e) => setTimezone(e.currentTarget.value)}
      />
      <Button
        loading={create.isPending}
        disabled={!name || !orgId}
        onClick={() =>
          create.mutate(
            { org_id: orgId, name, environment, timezone },
            {
              onSuccess: (project) => {
                notifications.show({ message: `Created ${project.name}`, color: 'teal' });
                modals.closeAll();
                onDone(project.id);
              },
              onError: (e) =>
                notifications.show({ message: (e as Error).message, color: 'red' }),
            },
          )
        }
      >
        Create project
      </Button>
    </Stack>
  );
}
